import React, { useEffect, useState } from 'react';
import { Col, Label, FormText, UncontrolledTooltip } from 'reactstrap';
import './FormInput.scss';
import { IconInfoCircle } from '@tabler/icons-react';
import { TextInput } from './Types/TextInput';
import { SelectInput } from './Types/SelectInput';
import { CheckboxInput } from './Types/CheckboxInput';
import { ZipcodeInput } from './Types/ZipcodeInput';
import { useFormInputValidation } from './useFormInputValidation';
import { HelperText } from './HelperText';
import { LengthIndicator } from './LengthIndicator';
import { toKebabCase } from '../../../shared/utility';

function FormInput(props) {
    let inputElement;
    const [currentTextContent, setCurrentTextContent] = useState('');
    const [helperFieldId, setHelperFieldId] = useState('');
    const [labelId, setLabelId] = useState('');
    const [helpDetailsId, setHelpDetailsId] = useState('');

    useEffect(() => {
        if (props.id) {
            setHelperFieldId(`${props.id}-helperText`);
            setLabelId(`${props.id}-label`);
            setHelpDetailsId(`${props.id}-helpDetails`);
        } else {
            setHelperFieldId(`helperText-${crypto.randomUUID()}`);
            setLabelId(`label-${crypto.randomUUID()}`);
            setHelpDetailsId(`helpDetails-${crypto.randomUUID()}`);
        }
    }, [props.id]);

    let sharedLabelAttr = {
        name: props.elementConfig.label,
        id: props.name,
        'aria-describedby': helperFieldId,
        'aria-label': props.elementConfig.label,
        'aria-labelledby': labelId,
    };

    const { isWarning, isInvalid, isValid, isRequired, helperText, helperType } = useFormInputValidation(props);

    // Warning state needs its own analytics hook per field, so a warning can be attributed to
    // a specific input
    const warningAnalyticsClassName = props.name
        ? `anl_input--warning--${toKebabCase(props.name)}`
        : 'anl_input--warning';

    switch (props.elementType) {
        case 'input':
        case 'textarea': {
            inputElement = (
                <TextInput
                    elementType={props.elementType}
                    elementConfig={props.elementConfig}
                    analyticsClassName={props.analyticsClassName}
                    warningAnalyticsClassName={warningAnalyticsClassName}
                    isInvalid={isInvalid}
                    isValid={isValid}
                    isWarning={isWarning}
                    value={props.value}
                    sharedLabelAttr={sharedLabelAttr}
                    onChange={onChange}
                    autoComplete={props.autoComplete}
                    inlineIcon={props.inlineIcon}
                    required={isRequired}
                />
            );
            break;
        }

        case 'select': {
            inputElement = (
                <SelectInput
                    elementConfig={props.elementConfig}
                    analyticsClassName={props.analyticsClassName}
                    value={props.value}
                    sharedLabelAttr={sharedLabelAttr}
                    changed={props.changed}
                    disabled={props.disabled}
                    required={isRequired}
                />
            );
            break;
        }

        case 'zipcode': {
            inputElement = (
                <ZipcodeInput
                    elementConfig={props.elementConfig}
                    analyticsClassName={props.analyticsClassName}
                    warningAnalyticsClassName={warningAnalyticsClassName}
                    value={props.value}
                    sharedLabelAttr={sharedLabelAttr}
                    onChange={onChange}
                    isInvalid={isInvalid}
                    isValid={isValid}
                    isWarning={isWarning}
                    inlineIcon={props.inlineIcon}
                    required={isRequired}
                />
            );
            break;
        }

        default: {
            inputElement = (
                <TextInput
                    elementType={props.elementType}
                    elementConfig={props.elementConfig}
                    analyticsClassName={props.analyticsClassName}
                    warningAnalyticsClassName={warningAnalyticsClassName}
                    isInvalid={isInvalid}
                    isValid={isValid}
                    isWarning={isWarning}
                    value={props.value}
                    sharedLabelAttr={sharedLabelAttr}
                    onChange={onChange}
                    autoComplete={props.autoComplete}
                    inlineIcon={props.inlineIcon}
                    required={isRequired}
                />
            );
            break;
        }
    }

    function onChange(event) {
        setCurrentTextContent(event.target.value);
        props.changed(event);
    }

    const tooltip = props.tooltip ? (
        <UncontrolledTooltip
            placement="bottom"
            trigger="focus"
            target={props.name}
            className="w-100"
            dangerouslySetInnerHTML={createMarkup(props.tooltip)}
        />
    ) : null;

    const lengthIndicator = (
        <LengthIndicator
            maxLenght={props.maxLenght}
            currentTextContent={currentTextContent}
            shouldValidate={props.shouldValidate}
            touched={props.touched}
        />
    );

    function createMarkup(text) {
        return { __html: text };
    }

    const renderedHelperText = (
        <HelperText helperType={helperType} helperText={helperText} helperFieldId={helperFieldId} />
    );

    if (props.elementType === 'checkbox') {
        return (
            <CheckboxInput
                elementConfig={props.elementConfig}
                analyticsClassName={props.analyticsClassName}
                value={props.value}
                sharedLabelAttr={sharedLabelAttr}
                invalid={props.invalid}
                touched={props.touched}
                shouldValidate={props.shouldValidate}
                changed={props.changed}
                labelId={labelId}
                renderedHelperText={renderedHelperText}
                createMarkup={createMarkup}
                required={isRequired}
            />
        );
    }

    let HelpDetails = () => <></>;

    if (props.helpDetails) {
        HelpDetails = () => (
            <>
                <IconInfoCircle id={helpDetailsId} size={18} className="mx-1 align-self-baseline" />
                {helpDetailsId && (
                    <UncontrolledTooltip
                        target={helpDetailsId}
                        container="root"
                        dangerouslySetInnerHTML={createMarkup(props.helpDetails)}
                        autohide={false}
                    />
                )}
                <div className="visually-hidden" dangerouslySetInnerHTML={createMarkup(props.helpDetails)} />
            </>
        );
    }

    return (
        <Col className={props.wrapperCSSClass || 'mb-3'} md={props.elementConfig.columns}>
            <div className="form-text-input">
                <Label for={sharedLabelAttr.id} id={labelId}>
                    {props.elementConfig.label}
                    <HelpDetails />
                </Label>
                {inputElement}
                <span aria-live="assertive" role="status">
                    {renderedHelperText}
                </span>
                {lengthIndicator}
                {props.formText ? <FormText>{props.formText}</FormText> : null}
                {tooltip}
            </div>
        </Col>
    );
}

export default FormInput;
